import Peer, { type DataConnection, type PeerError } from "peerjs";
import type { Profile, RoomState } from "./model";
import { addMessage, addPoints, dropMember, freshRoom, upsertMember, viewFor } from "./store";
import {
  continueRound,
  guessRole,
  houseAct,
  judgePick,
  playFromHand,
  seatHouseFamily,
  startNight,
  welcomeLate,
} from "./rules";

export type Packet =
  | { t: "hello"; profile: Profile }
  | { t: "state"; state: RoomState }
  | { t: "chat"; text: string; from: string }
  | { t: "fridge"; item: string; from: string }
  | { t: "start"; from: string }
  | { t: "play"; from: string; cardId: string }
  | { t: "judge"; from: string; playId: string }
  | { t: "next"; from: string }
  | { t: "guess"; from: string; targetId: string; roleId: string };

const PEER_OPTS = {
  host: "0.peerjs.com",
  port: 443,
  path: "/",
  secure: true,
} as const;

export function hostPeerId(room: string): string {
  const slug = room
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
  const safe = slug.length ? slug : "family-hq";
  return `fh${safe}hq`;
}

export class PeerRoom {
  private peer: Peer | null = null;
  private readonly guests = new Map<string, DataConnection>();
  private hostConn: DataConnection | null = null;
  private houseTimer = 0;
  private lolTimer = 0;
  private startTimer = 0;
  host = false;
  selfId = "";
  state: RoomState | null = null;
  status = "idle";

  constructor(
    readonly room: string,
    private draft: Omit<Profile, "id" | "score">,
    private readonly onChange: () => void,
    private readonly onStatus: (s: string) => void,
  ) {}

  connect(): void {
    this.setStatus("saving you a seat…");
    const hostId = hostPeerId(this.room);
    const attempt = new Peer(hostId, PEER_OPTS);
    this.peer = attempt;
    attempt.on("error", (err: PeerError<string>) => {
      if (err.type === "unavailable-id") {
        attempt.destroy();
        this.joinGuest(hostId);
        return;
      }
      this.setStatus(`room glitch: ${err.type}`);
    });
    attempt.on("open", (id) => {
      this.selfId = id;
      this.host = true;
      const me = { ...this.draft, id, score: 0 };
      this.state = freshRoom(this.room, me);
      addMessage(this.state, {
        from: "hq",
        kind: "system",
        text: `${me.name} opened Family HQ. Weirdos welcome.`,
      });
      this.setStatus(`hosting ${this.room}`);
      this.wireHost(attempt);
      this.onChange();
      this.startTimer = window.setTimeout(() => {
        if (this.state?.game.phase === "lobby") this.dispatch({ t: "start", from: this.selfId });
      }, 700);
    });
  }

  dispose(): void {
    window.clearTimeout(this.houseTimer);
    window.clearTimeout(this.lolTimer);
    window.clearTimeout(this.startTimer);
    this.hostConn?.close();
    for (const conn of this.guests.values()) conn.close();
    this.guests.clear();
    this.peer?.destroy();
    this.peer = null;
  }

  sendChat(text: string): void {
    this.dispatch({ t: "chat", text, from: this.selfId });
  }

  sendFridge(item: string): void {
    this.dispatch({ t: "fridge", item, from: this.selfId });
  }

  sendStart(): void {
    this.dispatch({ t: "start", from: this.selfId });
  }

  sendPlay(cardId: string): void {
    this.dispatch({ t: "play", from: this.selfId, cardId });
  }

  sendJudge(playId: string): void {
    this.dispatch({ t: "judge", from: this.selfId, playId });
  }

  sendNext(): void {
    this.dispatch({ t: "next", from: this.selfId });
  }

  sendGuess(targetId: string, roleId: string): void {
    this.dispatch({ t: "guess", from: this.selfId, targetId, roleId });
  }

  private joinGuest(hostId: string): void {
    this.setStatus("Family HQ exists — sneaking in…");
    const guest = new Peer(PEER_OPTS);
    this.peer = guest;
    guest.on("error", (err: PeerError<string>) => {
      this.setStatus(`couldn’t find a host (${err.type}). try again to open it.`);
    });
    guest.on("open", (id) => {
      this.selfId = id;
      this.host = false;
      const conn = guest.connect(hostId, { reliable: true, serialization: "json" });
      this.hostConn = conn;
      conn.on("open", () => {
        const me = { ...this.draft, id, score: 0 };
        conn.send({ t: "hello", profile: me } satisfies Packet);
        this.setStatus(`in ${this.room}`);
      });
      conn.on("data", (data) => this.onPacket(data as Packet));
      conn.on("close", () => this.setStatus("host wandered off. refresh to reopen Family HQ."));
    });
  }

  private wireHost(peer: Peer): void {
    peer.on("connection", (conn) => {
      conn.on("open", () => {
        this.guests.set(conn.peer, conn);
        if (this.state) conn.send({ t: "state", state: viewFor(this.state, conn.peer) } satisfies Packet);
      });
      conn.on("data", (data) => this.onPacket(data as Packet));
      conn.on("close", () => {
        this.guests.delete(conn.peer);
        if (!this.state) return;
        const name = this.state.members.find((m) => m.id === conn.peer)?.name ?? "someone";
        dropMember(this.state, conn.peer);
        addMessage(this.state, { from: "hq", kind: "system", text: `${name} went to get snacks.` });
        this.broadcastState();
        this.onChange();
      });
    });
  }

  private dispatch(packet: Packet): void {
    if (this.host) {
      this.apply(packet);
      this.kick();
      return;
    }
    this.hostConn?.send(packet);
  }

  private onPacket(packet: Packet): void {
    if (packet.t === "state") {
      this.state = packet.state;
      this.onChange();
      return;
    }
    if (this.host) {
      this.apply(packet);
      this.kick();
    }
  }

  private kick(): void {
    this.broadcastState();
    this.onChange();
    if (this.state?.game.phase === "lol") this.queueLol();
    else {
      window.clearTimeout(this.lolTimer);
      this.queueHouse();
    }
  }

  private apply(packet: Packet): void {
    if (!this.state) return;
    if (packet.t === "hello") {
      upsertMember(this.state, packet.profile);
      welcomeLate(this.state.game, packet.profile.id);
      addMessage(this.state, {
        from: "hq",
        kind: "system",
        text: `${packet.profile.name} sat down.`,
      });
      return;
    }
    if (packet.t === "chat") {
      addMessage(this.state, { from: packet.from, kind: "chat", text: packet.text });
      return;
    }
    if (packet.t === "fridge") {
      this.state.fridge.push(packet.item);
      addPoints(this.state, packet.from, 3);
      addMessage(this.state, { from: packet.from, kind: "chat", text: `stuck ${packet.item} on the fridge` });
      return;
    }
    if (packet.t === "start") {
      const humans = this.state.members
        .filter((m) => !m.bot && !m.left)
        .map((m) => ({ ...m, score: 0 }));
      this.state.members = seatHouseFamily(humans);
      this.state.game = startNight(this.state.members);
      addMessage(this.state, { from: "hq", kind: "system", text: "Game night. Tap a card. Don’t tell Mom." });
      return;
    }
    if (packet.t === "play") {
      const err = playFromHand(this.state.game, this.state.members, packet.from, packet.cardId);
      if (err) addMessage(this.state, { from: "hq", kind: "hq", text: err });
      return;
    }
    if (packet.t === "judge") {
      const err = judgePick(this.state.game, this.state.members, packet.from, packet.playId);
      if (err) addMessage(this.state, { from: "hq", kind: "hq", text: err });
      else addMessage(this.state, { from: "hq", kind: "system", text: this.state.game.narration });
      return;
    }
    if (packet.t === "next") {
      continueRound(this.state.game, this.state.members);
      return;
    }
    if (packet.t === "guess") {
      const line = guessRole(this.state.game, this.state.members, packet.from, packet.targetId, packet.roleId);
      this.state.game.narration = line;
      addMessage(this.state, { from: "hq", kind: "system", text: line });
    }
  }

  private queueHouse(): void {
    if (!this.host) return;
    window.clearTimeout(this.houseTimer);
    this.houseTimer = window.setTimeout(() => {
      if (!this.state) return;
      if (houseAct(this.state.game, this.state.members)) {
        this.kick();
      }
    }, 700);
  }

  private queueLol(): void {
    if (!this.host) return;
    window.clearTimeout(this.lolTimer);
    this.lolTimer = window.setTimeout(() => {
      if (this.state?.game.phase !== "lol") return;
      this.apply({ t: "next", from: this.selfId });
      this.kick();
    }, 4200);
  }

  private broadcastState(): void {
    if (!this.state) return;
    for (const conn of this.guests.values()) {
      if (conn.open) conn.send({ t: "state", state: viewFor(this.state, conn.peer) } satisfies Packet);
    }
  }

  private setStatus(s: string): void {
    this.status = s;
    this.onStatus(s);
  }
}
